import React from 'react';
import { Play, Clock, Video as VideoIcon, Radio, Link as LinkIcon, Share2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { VideosAPI } from '../api';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_META = {
  upload:    { label: 'Uploaded',  icon: VideoIcon,  color: 'text-emerald-700 border-emerald-500/30 bg-emerald-500/10' },
  recording: { label: 'Recorded',  icon: Radio,      color: 'text-rose-700 border-rose-500/30 bg-rose-500/10' },
  youtube:   { label: 'YouTube',   icon: LinkIcon,   color: 'text-sky-600 border-sky-500/30 bg-sky-500/10' },
};

export default function VideoCard({ video, onPlay, showPlayer, player }) {
  const source = video.source || 'youtube';
  const meta = SOURCE_META[source] || SOURCE_META.youtube;
  const SIcon = meta.icon;
  const { toast } = useToast();

  const isHosted = source !== 'youtube';
  const thumb = isHosted ? null : `https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`;
  const streamUrl = isHosted ? `${BACKEND_URL}/api/videos/${video.id}/stream` : null;

  const shareClip = async (e) => {
    e.stopPropagation();
    try {
      const res = await VideosAPI.share(video.id);
      const url = window.location.origin + res.url_path;
      await navigator.clipboard.writeText(url);
      toast({ title: 'Share link copied', description: 'Anyone with the link can view the clip and its tags.' });
    } catch (err) {
      toast({ title: 'Could not create share link', description: err?.response?.data?.detail || 'Try again.' });
    }
  };

  return (
    <div className="group relative bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-sky-500/40 transition-all hover:-translate-y-0.5">
      <button type="button" onClick={onPlay} className="w-full text-left" data-testid={`video-card-${video.id}`}>
        <div className="relative aspect-video bg-slate-900 overflow-hidden">
          {isHosted ? (
            <video src={streamUrl} muted preload="metadata"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <img src={thumb} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-14 w-14 rounded-full bg-sky-500/90 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform pulse-ring">
              <Play className="h-6 w-6 text-slate-900 ml-0.5" fill="currentColor" />
            </div>
          </div>
          <div className={`absolute top-2 left-2 flex items-center gap-1 border px-1.5 py-0.5 rounded text-[10px] font-semibold ${meta.color}`}>
            <SIcon className="h-3 w-3" /> {meta.label}
          </div>
          {video.duration && (
            <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur px-2 py-0.5 rounded text-xs text-slate-900 flex items-center gap-1">
              <Clock className="h-3 w-3" /> {video.duration}
            </div>
          )}
        </div>
        <div className="p-4">
          {showPlayer && player && (
            <div className="text-xs text-sky-600 mb-1">{player.firstName} {player.lastName}  #{player.jerseyNumber}</div>
          )}
          <div className="font-semibold text-slate-900 line-clamp-1">{video.title}</div>
          <div className="text-xs text-slate-500 mt-1">{new Date(video.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
          <div className="mt-2 flex flex-wrap gap-1">
            {video.tags?.map(t => <Badge key={t} className="bg-slate-100 text-slate-600 border-slate-300 text-[10px]">{t}</Badge>)}
          </div>
        </div>
      </button>

      {/* Share button - floats over card */}
      <button type="button" onClick={shareClip}
        title="Copy public share link"
        data-testid={`video-share-${video.id}`}
        className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/95 hover:bg-sky-50 border border-slate-200 flex items-center justify-center text-slate-700 hover:text-sky-600 shadow-sm transition-colors">
        <Share2 className="h-4 w-4" />
      </button>
    </div>
  );
}
